import { useNavigate } from "@remix-run/react";
import { Minus, Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "~/components/ui/Button";
import { cn } from "~/lib/utils";
import { VoucherInfo } from "~/stores/bookingCart";

const GuestsSelect = ({
  onConfirm,
  onChange,
  onCancel,
  disableConfirm = false,
  defaultMaleCount = 0,
  defaultFemaleCount = 0,
  guestLimitingVouchers = [],
  backUrl,
}: {
  disableConfirm?: boolean;
  onConfirm: (maleCount: number, femaleCount: number) => void;
  onChange: () => void;
  onCancel: () => void;
  defaultMaleCount?: number;
  defaultFemaleCount?: number;
  // Voucher che vincolano il numero di ospiti (ingressi e pacchetti): il totale
  // ospiti deve corrispondere alla somma dei loro NrPaxInvolved.
  guestLimitingVouchers?: VoucherInfo[];
  backUrl?: string;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [maleQuantity, setMaleQuantity] = useState(defaultMaleCount);
  const [femaleQuantity, setFemaleQuantity] = useState(defaultFemaleCount);

  const requiredGuests = useMemo(() => {
    if (guestLimitingVouchers.length === 0) return null;
    return guestLimitingVouchers.reduce((sum, voucher) => {
      return sum + (voucher.voucherData?.NrPaxInvolved || 0);
    }, 0);
  }, [guestLimitingVouchers]);

  const totalGuests = maleQuantity + femaleQuantity;
  const hasGuestLimit = requiredGuests !== null && requiredGuests > 0;
  const guestsMatchRequired =
    !hasGuestLimit || totalGuests === requiredGuests;

  useEffect(() => {
    setMaleQuantity(0);
    setFemaleQuantity(0);
  }, []);

  return (
    <div className="w-full max-w-xl mx-auto flex flex-col px-8 py-8">
      <h3 className="font-bold text-xl text-center">{t("Ospiti")}</h3>
      <p className="text-center mt-2">
        {t("Un'esperienza per sé o da condividere")}
      </p>
      {hasGuestLimit && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mt-4">
          <p className="text-sm font-semibold text-blue-900">
            {t("I tuoi voucher richiedono esattamente {{count}} ospiti", {
              count: requiredGuests,
            })}
          </p>
        </div>
      )}

      <div className="flex flex-col space-y-4 mt-6">
        <SelectGuestRow
          label={t("Uomini")}
          quantity={maleQuantity}
          maxGuests={requiredGuests}
          currentTotal={totalGuests}
          setQuantity={(q: number) => {
            onChange();
            setMaleQuantity(q);
          }}
        />
        <SelectGuestRow
          label={t("Donne")}
          quantity={femaleQuantity}
          maxGuests={requiredGuests}
          currentTotal={totalGuests}
          setQuantity={(q: number) => {
            onChange();
            setFemaleQuantity(q);
          }}
        />
      </div>

      {hasGuestLimit && !guestsMatchRequired && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mt-4">
          <p className="text-sm font-semibold text-red-900">
            {t(
              "Hai selezionato {{total}} ospiti, ma i tuoi voucher richiedono {{required}} ospiti",
              { total: totalGuests, required: requiredGuests },
            )}
          </p>
        </div>
      )}

      <p className="mt-4 font-bold">
        {t(
          "Il biglietto d'ingresso comprende l'accesso a tutte le aree della SPA",
        )}
        .
      </p>

      <p className="mt-2 italic">
        {t(
          "Conoscere il genere degli ospiti ci permette di garantire la corretta disponibilità degli spogliatoi.",
        )}
        <br />
        {t(
          "L'ingresso include l'accesso alla SPA e il Welcome Kit (accappatoio, telo da rendere a fine esperienza e ciabatte).",
        )}
        <br />
        {t("L'accesso non è consentito ai minori di 18 anni.")}
      </p>

      <div className="w-full flex flex-row items-center justify-center space-x-2 mt-4">
        {backUrl && (
          <Button
            variant={"muted"}
            onClick={() => {
              navigate(backUrl);
            }}
          >
            <span>{t("Indietro")}</span>
          </Button>
        )}
        <Button
          disabled={
            (maleQuantity === 0 && femaleQuantity === 0) ||
            disableConfirm ||
            !guestsMatchRequired
          }
          variant={"secondary"}
          onClick={() => {
            onConfirm(maleQuantity, femaleQuantity);
          }}
        >
          <span>{t("Conferma")}</span>
        </Button>
      </div>
    </div>
  );
};

const SelectGuestRow = ({
  label,
  quantity = 0,
  setQuantity,
  maxGuests = null,
  currentTotal = 0,
}: {
  label: string;
  quantity: number;
  setQuantity: any;
  maxGuests?: number | null;
  currentTotal?: number;
}) => {
  const canIncrease = maxGuests === null || currentTotal < maxGuests;

  return (
    <div
      id="step-guests-select"
      className={cn(
        "flex flex-row bg-white shadow-md rounded-xl py-3 px-4",
        {},
      )}
    >
      <span className="block flex-1 pr-4 font-semibold">{label}</span>
      <div className="flex flex-row">
        <Button
          onClick={() => {
            if (quantity > 0) {
              setQuantity((q: number) => q - 1);
            }
          }}
          variant={"secondary"}
          className="rounded-full mr-2"
          size="icon"
        >
          <Minus />
        </Button>
        <span className="w-5 flex flex-col items-center font-semibold">
          {quantity}
        </span>
        <Button
          onClick={() => {
            setQuantity((q: number) => q + 1);
          }}
          disabled={!canIncrease}
          variant={"secondary"}
          className="rounded-full ml-2"
          size="icon"
        >
          <Plus />
        </Button>
      </div>
    </div>
  );
};
export default GuestsSelect;
